import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one per-record call to the participant's own zone (ADR-122 Pattern 1).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ANY". A beta participant is an Account, which exists
 * in exactly one zone; resending an activation or extending a trial for a us-east-1
 * participant against ap-southeast-2 would 404 on a row the operator is looking at.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * Typed wrappers around the admin-api staff onboarding-launch routes (BOW / SOW /
 * POW / B2S launch tokens + the partner-picker).
 *
 * The one-shot beta-site provisioning wrappers (`provisionBetaSite` /
 * `listBetaSites`, POST /admin/beta/provision + GET /admin/beta/sites) were
 * removed with the Nexa Config "Beta Testing" tab in the ADR-080 Am.1 build —
 * that surface is superseded by the Start BOW dashboard workflow. The admin-api
 * handlers remain (role-gated) pending a focused follow-up removal.
 *
 * All routes here are gated server-side; the portal gate is convenience, never
 * the sole control.
 */

// ---------------------------------------------------------------------------
// ADR-073 Amendment 2 — BOW (Beta Onboarding Workflow) launch + dashboard.
// ---------------------------------------------------------------------------

export interface BowLaunchResult {
  bowUrl: string;
  token: string;
  expiresAt: string;
}

/**
 * Mint a BOW launch token (admin-run, account-less beta onboarding).
 * ADR-076: the staff dashboard mounts the §9-forked OBW engine IN PLACE
 * (StaffObwModal) and seeds this `token` so bow-complete provisions via
 * portal-api POST /portal/bow/provision — it no longer opens `bowUrl` in a
 * subscriber-portal tab. `bowUrl` is retained for the future unauthenticated /
 * partner mount. POST /admin/bow/launch → customers:manage.
 */
export function launchBow(
  /**
   * The zone this journey will CREATE its account in. Omitted = the home zone.
   *
   * ⚠ THE MINT IS ONLY HALF THE JOURNEY. This routes `POST /admin/bow/launch`; the account is
   * created later by `POST /portal/bow/provision`, which reads its origin from the same zone
   * (`PortalZone.portalApiBaseUrl`, seeded by `StaffObwModal`). Routing one without the other
   * would mint here and create there.
   */
  zone?: PortalZone,
): Promise<BowLaunchResult> {
  return call(zone, '/admin/bow/launch', { method: 'POST', body: JSON.stringify({}) });
}

export interface SowLaunchResult {
  sowUrl: string;
  token: string;
  expiresAt: string;
}

/**
 * Mint a SOW launch token (admin-run, account-less card-first subscriber
 * onboarding). ADR-073 / ADR-076: the staff dashboard mounts the §9-forked OBW
 * engine IN PLACE (StaffObwModal) and seeds this `token` so sow-complete
 * provisions via portal-api POST /portal/sow/provision. POST /admin/sow/launch →
 * customers:manage. Mirror of launchBow.
 */
export function launchSow(
  /** The zone this journey will CREATE its account in. Omitted = the home zone. See launchBow. */
  zone?: PortalZone,
): Promise<SowLaunchResult> {
  return call(zone, '/admin/sow/launch', { method: 'POST', body: JSON.stringify({}) });
}

// ---------------------------------------------------------------------------
// ADR-076 Option 2 — admin-assisted POW (partner-apply for an EXISTING subscriber)
// ---------------------------------------------------------------------------

export interface PartnerCandidate {
  accountId: string;
  customerNumber: string;
  businessName: string;
  contactName: string;
  email: string;
  /** Already a partner (decided-blocking brief) — render greyed + unclickable. */
  isPartner: boolean;
  /** ADR-077 A1.3 — has a NON-TERMINAL in-progress application: pickable, but
   *  the picker shows the continue/restart fork on pick (not a straight launch). */
  hasInProgressApplication?: boolean;
  inProgressApplicationId?: string | null;
  inProgressStatus?: string | null;
}

/** The subscriber picker data (pa-pick-subscriber). GET /admin/pow/partner-candidates. */
export function listPartnerCandidates(): Promise<{ items: PartnerCandidate[] }> {
  return adminApi('/admin/pow/partner-candidates');
}

export interface PowLaunchResult {
  token: string;
  expiresAt: string;
  prefill: { customerNumber: string; email: string; businessName: string; websiteUrl: string };
}

/**
 * Mint an admin-assisted POW launch token for the PICKED subscriber. POW is
 * subscriber-keyed (a partner is a subscriber-who-resells), so unlike BOW/SOW
 * this carries the chosen subscriber's accountId; the seam resolves their
 * identity server-side and signs it in. POST /admin/pow/launch → customers:manage.
 * The staff partner-apply fork seeds the token so /portal/pow/provision keys the
 * brief to the PICKED subscriber.
 */
export function launchPow(accountId: string): Promise<PowLaunchResult> {
  return adminApi('/admin/pow/launch', { method: 'POST', body: JSON.stringify({ accountId }) });
}

export type BetaParticipantStatus = 'waiting' | 'activated';

/** ADR-078 §2 — the derived lifecycle tag (no stored betaStage field). */
export type BetaLifecycleTag = 'BETA' | 'EXPIRED' | 'EXTENDED' | 'COMPLETED';

export interface BetaParticipant {
  accountId: string;
  businessName: string;
  ownerName: string;
  customerNumber: string;
  email: string;
  cognitoSub: string;
  primaryDomain: string;
  /** Onboarding Nexa consultant — a staff displayName (never email/ID). */
  consultant: string;
  /** POW/partner UAT punchlist item 8 — the consultant's email, shown stacked
   *  below the name on the dashboard. Blank on rows provisioned before the field
   *  existed (recovered by the beta-consultant backfill). */
  consultantEmail: string;
  onboardedAt: string;
  /** Step 1 — account confirmed / password set (Cognito CONFIRMED). */
  status: BetaParticipantStatus;
  /** Step 2 — snippet installed and beacons arriving (Tinybird first-beacon). */
  telemetryLive: boolean;
  /**
   * Signed Status-Age triage day count:
   *   - negative → days validated while the snippet has NOT fired (waiting on install)
   *   - positive → days the site has been live since the first beacon
   *   - null     → not yet validated (no basis; renders "—")
   */
  statusAgeDays: number | null;
  // ── ADR-078 trial clock + lifecycle tag ───────────────────────────────────
  /** Derived 4-state lifecycle tag. */
  lifecycleTag: BetaLifecycleTag;
  /** First-beacon clock start (lazily stamped server-side); null pre-clock. */
  betaTrialStartedAt: string | null;
  /** Derived expiry = start + 28d × (1 + extensionCount); null pre-clock. */
  betaTrialExpiresAt: string | null;
  /** 0 or 1 — the single allowed extension. */
  betaExtensionCount: number;
  /**
   * Signed trial countdown once the clock runs (positive = days remaining,
   * negative = days since expiry); null before the first beacon. When non-null
   * it replaces the pre-clock statusAgeDays meaning in the Status-age column.
   */
  trialDaysRemaining: number | null;

  /**
   * ADR-122 Pattern 1 — which zone this participant actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server. Always populated for a
   * row that came through `listAllBetaParticipants`.
   */
  zone?: string;
}

export interface BetaParticipantsResult {
  participants: BetaParticipant[];
  awaiting: number;
  activated: number;
}

/** Onboarding-dashboard "Beta participants" panel (Validated · Telemetry · Status Age). */
export function listBetaParticipants(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BetaParticipantsResult> {
  return call(zone, '/admin/beta/participants');
}

export interface BetaParticipantsFanout extends BetaParticipantsResult {
  /** Labels of zones that did not answer. Empty on a complete read. */
  failedZones: string[];
}

/**
 * Every beta participant, across every zone (ADR-122 Pattern 1).
 *
 * ⭐ THE ALERT TILES ON THIS SAME PAGE ALREADY FAN OUT (`fetchAlertCounts`); the roster beneath
 * them never did, so the dashboard reported platform-wide alert counts directly above a
 * participant list that was silently one jurisdiction's. Bringing the roster in line with the
 * rest of its own page is the whole of ADR-122's ask here.
 *
 * `awaiting` and `activated` are SUMMED rather than taken from one zone: they are counts of the
 * rows in the list, so a headline that disagreed with the list below it would be its own bug.
 */
export async function listAllBetaParticipants(): Promise<BetaParticipantsFanout> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await listBetaParticipants(zone);
    return [{ zone, r }];
  });
  // No zone answered → a failed read, not an empty programme.
  throwIfAllZonesFailed(results);

  const pages = results.flatMap((x) => x.items);
  return {
    participants: pages
      .flatMap(({ zone, r }) => r.participants.map((p) => ({ ...p, zone: zone.code })))
      // Newest first, so a merge of two zones reads as one roster rather than AU-then-US.
      .sort((a, b) => (b.onboardedAt || '').localeCompare(a.onboardedAt || '')),
    awaiting: pages.reduce((n, { r }) => n + r.awaiting, 0),
    activated: pages.reduce((n, { r }) => n + r.activated, 0),
    failedZones: failedZoneLabels(results),
  };
}

/**
 * UAT punchlist H — fire a SYNTHETIC test beacon through the ingest path for the
 * participant's domain token, so Telemetry/Validated can be exercised without a
 * live website. Admin-only (server-side Cedar customers:manage).
 * POST /admin/beta/test-beacon.
 */
export function sendTestBeacon(
  accountId: string,
  /** The participant's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ ok: boolean; domainToken?: string }> {
  return call(zone, '/admin/beta/test-beacon', { method: 'POST', body: JSON.stringify({ accountId }) });
}

/** Resend the activation email for a waiting beta participant. */
export function resendBetaActivation(
  cognitoSub: string,
  /** The participant's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ resent: boolean; emailSent: boolean }> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}/resend`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// ADR-078 — beta lifecycle exits (Extend · Upgrade · Delete). All three are
// server-side Cedar customers:manage; the portal gate is convenience only.
// ---------------------------------------------------------------------------

/**
 * ADR-078 §3.1 — extend the trial by +28 days (the single allowed extension).
 * Server asserts isBeta && EXPIRED; increments betaExtensionCount 0→1.
 * POST /admin/beta/extend.
 */
export function extendBetaTrial(
  accountId: string,
  /** The participant's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ accountId: string; betaExtensionCount: number; lifecycleTag: BetaLifecycleTag; betaTrialExpiresAt: string | null }> {
  return call(zone, '/admin/beta/extend', { method: 'POST', body: JSON.stringify({ accountId }) });
}

/**
 * ADR-078 §4 — mint a B2S launch token for the target beta account (Upgrade →
 * beta → subscriber). Returns the admin-trusted launch URL into the subscriber
 * fork's B2S journey. POST /admin/b2s/launch.
 */
export function launchB2s(
  accountId: string,
  /** The participant's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ token: string; b2sUrl: string; accountId: string; businessName: string; expiresAt: string }> {
  return call(zone, '/admin/b2s/launch', { method: 'POST', body: JSON.stringify({ accountId }) });
}

/**
 * ADR-078 §5 — hard-delete a beta participant (beta accounts ONLY; server
 * asserts isBeta===true and refuses otherwise). Sends the branded deletion email
 * first, then cascades Cognito + DDB. POST /admin/beta/delete.
 */
export function deleteBetaParticipant(
  accountId: string,
  /**
   * The participant's own zone. Omitted = the home zone.
   *
   * ⚠ IRREVERSIBLE, SO THE ZONE MATTERS MOST HERE. A delete routed to the wrong zone is either
   * a 404 the operator reads as "already gone", or nothing happening at all while they believe
   * the account was removed.
   */
  zone?: PortalZone,
): Promise<{ accountId: string; deleted: boolean; domainsDeleted: number; emailSent: boolean }> {
  return call(zone, '/admin/beta/delete', { method: 'POST', body: JSON.stringify({ accountId }) });
}
